import React, { useEffect, useState } from "react";
import {
  FaFileAlt,
  FaDownload,
  FaUpload,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaExclamationTriangle,
  FaEye,
  FaFileContract,
  FaCalendarAlt,
} from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../core/common/header/PageHeader";

interface CourseRegulationDto {
  id: number;
  enrollmentId: number;
  courseId: number;
  courseTitleForStudent: string;
  status: "NOT_UPLOADED" | "PENDING_REVIEW" | "ACCEPTED" | "REJECTED";
  uploadedAt?: string;
  reviewedAt?: string;
  rejectionReason?: string;
  regulationPdfUrl?: string;
  uploadedFileDownloadUrl?: string;
  hasTemplate: boolean;
  enrollmentDate?: string;
  encouragedUploadDate?: string;
  daysUntilDeadline?: number;
}

const CourseRegulations: React.FC = () => {
  const { student } = useAuth();
  const API = process.env.REACT_APP_API_BASE_URL;

  const [regulations, setRegulations] = useState<CourseRegulationDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadingId, setUploadingId] = useState<number | null>(null);
  const [downloadingTemplateId, setDownloadingTemplateId] = useState<
    number | null
  >(null);
  const [viewingFileId, setViewingFileId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadRegulations();
  }, []);

  const loadRegulations = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API}/api/student/regulations`, {
        credentials: "include",
      });

      if (!response.ok) throw new Error("Failed to load regulations");

      const data = await response.json();
      setRegulations(data);
    } catch (err) {
      setError("Failed to load course regulations. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = async (courseId: number) => {
    try {
      setDownloadingTemplateId(courseId);
      const response = await fetch(
        `${API}/api/student/regulations/template/${courseId}/download-url`,
        {
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to get download URL");
      }

      const data = await response.json();
      window.open(data.downloadUrl, "_blank");
    } catch (err) {
      alert(
        "Failed to download regulation template. Please contact support if the problem persists."
      );
      console.error(err);
    } finally {
      setDownloadingTemplateId(null);
    }
  };

  const handleViewUploadedFile = async (regulationId: number) => {
    try {
      setViewingFileId(regulationId);
      const response = await fetch(
        `${API}/api/student/regulations/${regulationId}/download-url`,
        {
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to get download URL");
      }

      const data = await response.json();
      window.open(data.downloadUrl, "_blank");
    } catch (err) {
      alert("Failed to view uploaded file. Please try again.");
      console.error(err);
    } finally {
      setViewingFileId(null);
    }
  };

  const handleUpload = async (enrollmentId: number, file: File) => {
    if (!file) return;

    if (file.type !== "application/pdf") {
      alert("Please upload a PDF file only");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert("File size must be less than 10MB");
      return;
    }

    try {
      setUploadingId(enrollmentId);
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(
        `${API}/api/student/regulations/${enrollmentId}/upload`,
        {
          method: "POST",
          credentials: "include",
          body: formData,
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Upload failed");
      }

      alert("Regulation uploaded successfully! Waiting for admin review.");
      await loadRegulations(); // Reload to get updated status
    } catch (err: any) {
      alert(err.message || "Failed to upload regulation. Please try again.");
      console.error(err);
    } finally {
      setUploadingId(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "NOT_UPLOADED":
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold border border-gray-200">
            <FaExclamationTriangle className="w-3.5 h-3.5" />
            Not Uploaded
          </span>
        );
      case "PENDING_REVIEW":
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-50 text-amber-700 rounded-full text-xs font-semibold border border-amber-200">
            <FaClock className="w-3.5 h-3.5" />
            Pending Review
          </span>
        );
      case "ACCEPTED":
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-semibold border border-emerald-200">
            <FaCheckCircle className="w-3.5 h-3.5" />
            Accepted
          </span>
        );
      case "REJECTED":
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-rose-50 text-rose-600 rounded-full text-xs font-semibold border border-rose-200">
            <FaTimesCircle className="w-3.5 h-3.5" />
            Rejected
          </span>
        );
      default:
        return null;
    }
  };

  // ✅ NEW: Get urgency level based on days until deadline
  const getUrgencyLevel = (
    daysUntilDeadline: number | undefined,
    status: string
  ) => {
    if (status !== "NOT_UPLOADED") return "none";
    if (daysUntilDeadline === undefined) return "none";

    if (daysUntilDeadline < 0) return "overdue";
    if (daysUntilDeadline === 0) return "today";
    if (daysUntilDeadline <= 1) return "urgent";
    if (daysUntilDeadline <= 2) return "warning";
    return "normal";
  };

  // ✅ NEW: Get urgency banner for NOT_UPLOADED regulations
  const getUrgencyBanner = (regulation: CourseRegulationDto) => {
    if (regulation.status !== "NOT_UPLOADED") return null;

    const urgency = getUrgencyLevel(
      regulation.daysUntilDeadline,
      regulation.status
    );

    if (urgency === "overdue") {
      return (
        <div className="mb-4 bg-red-100 border-2 border-red-400 rounded-lg p-4 flex items-start gap-3">
          <FaExclamationTriangle className="w-5 h-5 text-red-700 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-red-800">
              Upload Deadline Passed!
            </p>
            <p className="text-sm text-red-700 mt-1">
              The encouraged upload period has ended. Please upload your signed
              regulation as soon as possible to complete your enrollment.
            </p>
          </div>
        </div>
      );
    }

    if (urgency === "today") {
      return (
        <div className="mb-4 bg-red-50 border-2 border-red-300 rounded-lg p-4 flex items-start gap-3">
          <FaClock className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-red-800">
              Upload Deadline is Today!
            </p>
            <p className="text-sm text-red-700 mt-1">
              Please upload your signed regulation today to complete your
              enrollment on time.
            </p>
          </div>
        </div>
      );
    }

    if (urgency === "urgent") {
      return (
        <div className="mb-4 bg-orange-50 border-2 border-orange-300 rounded-lg p-4 flex items-start gap-3">
          <FaClock className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-orange-800">
              Upload Deadline Approaching
            </p>
            <p className="text-sm text-orange-700 mt-1">
              Only {regulation.daysUntilDeadline}{" "}
              {regulation.daysUntilDeadline === 1 ? "day" : "days"} left to
              upload. Please complete this soon!
            </p>
          </div>
        </div>
      );
    }

    if (urgency === "warning") {
      return (
        <div className="mb-4 bg-amber-50 border border-amber-300 rounded-lg p-4 flex items-start gap-3">
          <FaClock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">
              Upload Reminder
            </p>
            <p className="text-sm text-amber-700 mt-1">
              {regulation.daysUntilDeadline} days remaining to upload your
              signed regulation.
            </p>
          </div>
        </div>
      );
    }

    return null;
  };

  // ✅ NEW: Get card border color based on urgency
  const getCardBorderClass = (regulation: CourseRegulationDto) => {
    if (regulation.status !== "NOT_UPLOADED")
      return "border-blue-200 from-blue-50 to-cyan-50";

    const urgency = getUrgencyLevel(
      regulation.daysUntilDeadline,
      regulation.status
    );

    switch (urgency) {
      case "overdue":
        return "border-red-400 from-red-50 to-rose-100";
      case "today":
        return "border-red-300 from-red-50 to-orange-50";
      case "urgent":
        return "border-orange-300 from-orange-50 to-amber-50";
      case "warning":
        return "border-amber-300 from-amber-50 to-yellow-50";
      default:
        return "border-blue-200 from-blue-50 to-cyan-50";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#009fe3] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading regulations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaFileContract />}
        title="Course Regulations"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/student/student-dashboard" },
          { label: "Course Regulations" },
        ]}
      />

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 py-10">
        {/* Welcome Section */}
        <div className="mb-10 bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white rounded-xl p-6 shadow-lg">
          <h2 className="text-3xl font-bold mb-2 text-white flex items-center gap-2">
            <FaFileContract />
            {student?.firstName
              ? `Welcome, ${student.firstName} 👋`
              : "Welcome 👋"}
          </h2>
          <p className="text-white opacity-90 text-base">
            Download course regulations, sign them, and upload within 3 days of
            enrollment for approval.
          </p>
        </div>

        {error && (
          <div className="mb-6 bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <FaTimesCircle />
            {error}
          </div>
        )}

        {regulations.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <FaFileAlt className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              No Course Enrollments
            </h3>
            <p className="text-gray-600">
              You don't have any course enrollments yet.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Instructions Card */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center gap-2">
                <FaFileAlt className="w-5 h-5" />
                How to Submit Your Course Regulation
              </h3>
              <ol className="list-decimal list-inside space-y-2 text-blue-800">
                <li>Download the course regulation PDF template</li>
                <li>Print and sign the document</li>
                <li>Scan the signed document as a PDF</li>
                <li>Upload the signed PDF using the upload button</li>
                <li>
                  <strong>
                    Complete within 3 days of enrollment (encouraged deadline)
                  </strong>
                </li>
                <li>Wait for admin approval</li>
              </ol>
            </div>

            {/* Regulations List */}
            {regulations.map((regulation) => (
              <div
                key={regulation.id}
                className={`bg-gradient-to-br ${getCardBorderClass(
                  regulation
                )} border-2 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 overflow-hidden`}
              >
                {/* Course Header */}
                <div className="bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white p-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold flex items-center gap-2">
                        <FaFileContract />
                        {regulation.courseTitleForStudent}
                      </h3>
                      <p className="text-cyan-100 text-sm mt-1">
                        Course Regulation Document
                      </p>
                    </div>
                    <div className="mt-4 md:mt-0">
                      {getStatusBadge(regulation.status)}
                    </div>
                  </div>
                </div>

                {/* Course Content */}
                <div className="p-6">
                  {/* ✅ NEW: Urgency Banner */}
                  {getUrgencyBanner(regulation)}

                  {/* No Template Warning */}
                  {!regulation.hasTemplate && (
                    <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                      <FaExclamationTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-amber-800">
                          Template Not Available
                        </p>
                        <p className="text-sm text-amber-700 mt-1">
                          The regulation template has not been uploaded by the
                          admin yet. Please check back later.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Rejection Reason */}
                  {regulation.status === "REJECTED" &&
                    regulation.rejectionReason && (
                      <div className="mb-4 bg-rose-50 border border-rose-200 rounded-lg p-4 flex items-start gap-3">
                        <FaTimesCircle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-rose-800 mb-1">
                            Rejection Reason:
                          </p>
                          <p className="text-sm text-rose-700">
                            {regulation.rejectionReason}
                          </p>
                          <p className="text-xs text-rose-600 mt-2">
                            Please address the issues and re-upload your signed
                            regulation.
                          </p>
                        </div>
                      </div>
                    )}

                  {/* ✅ NEW: Enrollment and Deadline Info */}
                  <div className="mb-4 bg-white border border-gray-200 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {regulation.enrollmentDate && (
                        <div className="flex items-center gap-2">
                          <FaCalendarAlt className="w-4 h-4 text-gray-500" />
                          <div>
                            <p className="text-xs text-gray-500 font-medium">
                              Enrollment Date
                            </p>
                            <p className="text-sm text-gray-700 font-semibold">
                              {new Date(
                                regulation.enrollmentDate
                              ).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              })}
                            </p>
                          </div>
                        </div>
                      )}

                      {regulation.encouragedUploadDate && (
                        <div className="flex items-center gap-2">
                          <FaClock className="w-4 h-4 text-gray-500" />
                          <div>
                            <p className="text-xs text-gray-500 font-medium">
                              Encouraged Upload By
                            </p>
                            <p
                              className={`text-sm font-semibold ${
                                getUrgencyLevel(
                                  regulation.daysUntilDeadline,
                                  regulation.status
                                ) === "overdue"
                                  ? "text-red-700"
                                  : getUrgencyLevel(
                                      regulation.daysUntilDeadline,
                                      regulation.status
                                    ) === "urgent" ||
                                    getUrgencyLevel(
                                      regulation.daysUntilDeadline,
                                      regulation.status
                                    ) === "today"
                                  ? "text-orange-700"
                                  : "text-gray-700"
                              }`}
                            >
                              {new Date(
                                regulation.encouragedUploadDate
                              ).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              })}
                              {regulation.status === "NOT_UPLOADED" &&
                                regulation.daysUntilDeadline !== undefined && (
                                  <span className="text-xs ml-1">
                                    (
                                    {regulation.daysUntilDeadline < 0
                                      ? `${Math.abs(
                                          regulation.daysUntilDeadline
                                        )} days overdue`
                                      : regulation.daysUntilDeadline === 0
                                      ? "today"
                                      : `${regulation.daysUntilDeadline} days left`}
                                    )
                                  </span>
                                )}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Timestamps */}
                  <div className="mb-4 space-y-2">
                    {regulation.uploadedAt && (
                      <p className="text-sm text-gray-600 flex items-center gap-2">
                        <FaClock className="w-4 h-4 text-gray-400" />
                        <span>
                          Uploaded on:{" "}
                          {new Date(regulation.uploadedAt).toLocaleDateString(
                            "en-US",
                            {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            }
                          )}
                        </span>
                      </p>
                    )}

                    {regulation.reviewedAt &&
                      regulation.status === "ACCEPTED" && (
                        <p className="text-sm text-emerald-600 flex items-center gap-2">
                          <FaCheckCircle className="w-4 h-4 text-emerald-500" />
                          <span>
                            Accepted on:{" "}
                            {new Date(regulation.reviewedAt).toLocaleDateString(
                              "en-US",
                              {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              }
                            )}
                          </span>
                        </p>
                      )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-3">
                    {/* Download Template Button */}
                    {regulation.hasTemplate && (
                      <button
                        onClick={() =>
                          handleDownloadTemplate(regulation.courseId)
                        }
                        disabled={downloadingTemplateId === regulation.courseId}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all shadow-sm ${
                          downloadingTemplateId === regulation.courseId
                            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                            : "bg-[#009fe3] text-white hover:bg-cyan-600 hover:shadow-md"
                        }`}
                      >
                        <FaDownload className="w-4 h-4" />
                        {downloadingTemplateId === regulation.courseId
                          ? "Downloading..."
                          : "Download Template"}
                      </button>
                    )}

                    {/* View Uploaded File */}
                    {regulation.uploadedFileDownloadUrl && (
                      <button
                        onClick={() => handleViewUploadedFile(regulation.id)}
                        disabled={viewingFileId === regulation.id}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all shadow-sm ${
                          viewingFileId === regulation.id
                            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                            : "bg-indigo-600 text-white hover:bg-indigo-700 hover:shadow-md"
                        }`}
                      >
                        <FaEye className="w-4 h-4" />
                        {viewingFileId === regulation.id
                          ? "Opening..."
                          : "View My Upload"}
                      </button>
                    )}

                    {/* Upload Button - Only show if not accepted and has template */}
                    {regulation.status !== "ACCEPTED" &&
                      regulation.hasTemplate && (
                        <label
                          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all shadow-sm ${
                            uploadingId === regulation.enrollmentId
                              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                              : "bg-emerald-600 text-white hover:bg-emerald-700 hover:shadow-md cursor-pointer"
                          }`}
                        >
                          <FaUpload className="w-4 h-4" />
                          {uploadingId === regulation.enrollmentId
                            ? "Uploading..."
                            : regulation.status === "NOT_UPLOADED"
                            ? "Upload Signed PDF"
                            : "Re-upload PDF"}
                          <input
                            type="file"
                            accept=".pdf"
                            className="hidden"
                            disabled={uploadingId === regulation.enrollmentId}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file && uploadingId === null) {
                                handleUpload(regulation.enrollmentId, file);
                              }
                              // Reset input value to allow re-selecting the same file
                              e.target.value = "";
                            }}
                          />
                        </label>
                      )}
                  </div>
                </div>

                {/* Status Footer */}
                {regulation.status === "ACCEPTED" && (
                  <div className="bg-emerald-50 px-6 py-3 border-t border-emerald-200">
                    <p className="text-sm text-emerald-700 font-medium flex items-center gap-2">
                      <FaCheckCircle className="w-4 h-4" />
                      Your regulation has been accepted. No further action
                      required.
                    </p>
                  </div>
                )}

                {regulation.status === "PENDING_REVIEW" && (
                  <div className="bg-amber-50 px-6 py-3 border-t border-amber-200">
                    <p className="text-sm text-amber-700 font-medium flex items-center gap-2">
                      <FaClock className="w-4 h-4" />
                      Your regulation is under review by the admin. You'll be
                      notified once it's processed.
                    </p>
                  </div>
                )}

                {regulation.status === "REJECTED" && (
                  <div className="bg-rose-50 px-6 py-3 border-t border-rose-200">
                    <p className="text-sm text-rose-600 font-medium flex items-center gap-2">
                      <FaTimesCircle className="w-4 h-4" />
                      Your regulation was rejected. Please review the feedback
                      above and re-upload.
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default CourseRegulations;
